import React from 'react';

/**
 * 统一图标体系。
 *
 * 为什么自建而不用现成图标字体/组件库：
 * 1. 去掉"AI 味"的关键不是换一套图，而是把线宽、网格、端点样式收敛成一个规格。
 *    这里统一 24 网格、1.5 线宽、round 端点、纯 currentColor 描边 ——
 *    没有渐变、没有发光、没有伪 3D，这三样正是生成式图标最容易露出的痕迹。
 * 2. 线宽定 1.5 而非常见的 2：参考站的分隔线、边框都是 hairline 级，
 *    图标线宽必须跟着压细，否则图标会比周围的结构线更"重"，看着像贴上去的。
 * 3. 业务语义图标（订阅周期、关系图谱、计薪、排名）通用库里没有精确对应，
 *    自建可以按产品真实含义组合基本形，而不是硬套一个近似图。
 *
 * 基础形取自 Lucide 的开源图形规范（ISC），保持与主流图标系统的视觉一致性。
 */

export interface IconProps {
  /** 像素尺寸，同时作用于宽高。默认 20，与正文字号同量级。 */
  size?: number;
  className?: string;
  /** 极少数场合（超大尺寸展示）需要再压细线宽，此时可覆盖。 */
  strokeWidth?: number;
  style?: React.CSSProperties;
}

type SvgAttrs = React.SVGProps<SVGSVGElement>;

/** 所有图标共享的 SVG 属性，确保规格不会在单个图标上走样。 */
function svgProps({ size = 20, className, strokeWidth = 1.5, style }: IconProps): SvgAttrs {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className,
    style,
    // 图标一律作为装饰处理，语义由相邻文本或 aria-label 承载
    'aria-hidden': true,
    focusable: false,
  };
}

/* ---------- 导航与通用 ---------- */

export const IconChevronDown: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const IconMenu: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

export const IconClose: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

/** 外链标识。新标签页打开的入口必须有可见提示，否则是可用性缺口。 */
export const IconExternalLink: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6" />
  </svg>
);

export const IconArrowRight: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

export const IconArrowUpRight: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M7 7h10v10" />
    <path d="M7 17 17 7" />
  </svg>
);

export const IconCheck: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const IconSearch: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

export const IconMail: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </svg>
);

/** 在线咨询/客服。耳机横梁 + 两侧耳罩，避免用"人头戴耳机"那类插画感图形 */
export const IconHeadset: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
    <path d="M4 14h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
    <path d="M20 14h-2a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1z" />
  </svg>
);

/* ---------- 五款产品 ---------- */

/** 订无忧：订阅是周期性重复的账单，用闭环箭头表达"到期又回来" */
export const IconSubscriptionCycle: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="m17 2 4 4-4 4" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <path d="m7 22-4-4 4-4" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

/** QuQu：以"我"为中心的人际关系图谱，用中心节点 + 外围连线表达 */
export const IconRelationGraph: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <circle cx="12" cy="12" r="2.5" />
    <circle cx="5" cy="5" r="2" />
    <circle cx="19" cy="5" r="2" />
    <circle cx="19" cy="19" r="2" />
    <path d="m10.3 10.3-3.8-3.8" />
    <path d="m13.7 10.3 3.8-3.8" />
    <path d="m13.7 13.7 3.8 3.8" />
  </svg>
);

/** 薪资奖励官：手表表盘 + 指针，产品形态本身就是 Apple Watch */
export const IconWatch: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <circle cx="12" cy="12" r="6" />
    <path d="M12 10v2l1.5 1.5" />
    <path d="m16.1 7.7-.8-4.1A2 2 0 0 0 13.3 2h-2.6a2 2 0 0 0-2 1.6l-.8 4.1" />
    <path d="m7.9 16.3.8 4.1a2 2 0 0 0 2 1.6h2.6a2 2 0 0 0 2-1.6l.8-4.1" />
  </svg>
);

/** 排名小程序：名次高低用不等高柱形表达，比奖杯更"数据"少"营销" */
export const IconRanking: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M3 21h18" />
    <path d="M6 21v-6" />
    <path d="M12 21V5" />
    <path d="M18 21v-9" />
  </svg>
);

/** 销售智能体：增长折线 */
export const IconGrowth: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M22 7 13.5 15.5 8.5 10.5 2 17" />
    <path d="M16 7h6v6" />
  </svg>
);

/* ---------- 功能语义 ---------- */

export const IconList: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M8 6h13M8 12h13M8 18h13" />
    <path d="M3 6h.01M3 12h.01M3 18h.01" />
  </svg>
);

export const IconChart: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M3 3v16a2 2 0 0 0 2 2h16" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

export const IconBell: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

/** 云同步。用云 + 双向箭头，区别于单纯的"上传" */
export const IconCloudSync: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M17.5 17H9A5 5 0 1 1 9.7 7.1 6 6 0 0 1 21 9.5a4 4 0 0 1-3.5 7.5" />
    <path d="M12 21v-6" />
    <path d="m9.5 17.5 2.5-2.5 2.5 2.5" />
  </svg>
);

export const IconShield: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  </svg>
);

export const IconLock: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/** 备份与恢复：存储盘 + 回退箭头 */
export const IconRestore: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 8v4l3 2" />
  </svg>
);

export const IconCalendar: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M8 2v4M16 2v4M3 10h18" />
  </svg>
);

export const IconWallet: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
    <path d="M17 15h.01" />
  </svg>
);

export const IconSmartphone: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <rect x="5" y="2" width="14" height="20" rx="2.5" />
    <path d="M11 18h2" />
  </svg>
);

/** 计算/引擎语义。方形芯片轮廓，避免用"大脑""闪电"这类拟人化图形 */
export const IconEngine: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <rect x="5" y="5" width="14" height="14" rx="2" />
    <rect x="9" y="9" width="6" height="6" />
    <path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
  </svg>
);

export const IconLayers: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M12 3 3 7.5l9 4.5 9-4.5z" />
    <path d="m3 12.5 9 4.5 9-4.5" />
    <path d="m3 17 9 4.5L21 17" />
  </svg>
);

export const IconUsers: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const IconTarget: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1" />
  </svg>
);

/* ---------- 关于页：愿景 / 里程碑 / 团队 / 文化 ---------- */

export const IconEye: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const IconFlag: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M5 15s1-1 4-1 5 2 8 2 3-1 3-1V4s-1 1-3 1-5-2-8-2-4 1-4 1Z" />
    <path d="M5 22v-7" />
  </svg>
);

/** 价值观：天平。用几何三角吊盘而不是拟物秤盘，保持与其它图标同一抽象层级 */
export const IconScale: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M12 3v17" />
    <path d="M8 20h8" />
    <path d="M4 7h16" />
    <path d="M4 7 1.5 13h5z" />
    <path d="M20 7l-2.5 6h5z" />
  </svg>
);

export const IconBuilding: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01" />
    <path d="M10 22v-4.5h4V22" />
  </svg>
);

export const IconPackage: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="m3.3 7 8.7 5 8.7-5" />
    <path d="M12 22V12" />
  </svg>
);

/** 双轨战略：一条主干分出两路 */
export const IconBranch: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M6 3v12" />
    <circle cx="18" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <path d="M18 9a9 9 0 0 1-9 9" />
  </svg>
);

export const IconCode: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="m16 18 6-6-6-6" />
    <path d="m8 6-6 6 6 6" />
  </svg>
);

export const IconPen: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    <path d="m15 5 4 4" />
  </svg>
);

export const IconCompass: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <circle cx="12" cy="12" r="9.5" />
    <path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36z" />
  </svg>
);

/** 创新：灯泡本体，不画放射光芒线 —— 光芒线是"发光图标"观感的主要来源 */
export const IconLightbulb: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5A5.5 5.5 0 1 0 7.5 11.5c.8.8 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </svg>
);

/* ---------- 新闻分类 ---------- */

/** 媒体报道 */
export const IconNewspaper: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M18 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h15a3 3 0 0 0 3-3V8h-4V4Z" />
    <path d="M18 4v13a3 3 0 0 0 3 3" />
    <path d="M6 8h8M6 12h8M6 16h5" />
  </svg>
);

/** 产品更新：闭环箭头。刻意不画成"刷新+齿轮"的复合图形，避免细节堆叠 */
export const IconRefresh: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-8.24-5.4" />
    <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 8.24 5.4" />
    <path d="M21 3v5.4h-5.4" />
    <path d="M3 21v-5.4h5.4" />
  </svg>
);

/* ---------- 表单、状态与工具 ---------- */

export const IconUser: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

export const IconClock: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </svg>
);

export const IconMessage: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M21 12a8 8 0 0 1-8 8H8l-4 3v-6.5A8 8 0 0 1 13 4a8 8 0 0 1 8 8z" />
  </svg>
);

export const IconHelpCircle: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.6 9.4a2.5 2.5 0 0 1 4.9.6c0 1.7-2.5 2-2.5 3.5" />
    <path d="M12 17h.01" />
  </svg>
);

export const IconAlertTriangle: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M10.7 3.9 2.5 18a1.5 1.5 0 0 0 1.3 2.3h16.4a1.5 1.5 0 0 0 1.3-2.3L13.3 3.9a1.5 1.5 0 0 0-2.6 0z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

/** 提交/发送。纸飞机保持单一轮廓 + 一道折线，不画内部阴影面 */
export const IconSend: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M21.5 2.5 2 10.2l7.3 2.5L11.8 20z" />
    <path d="M21.5 2.5 9.3 12.7" />
  </svg>
);

/**
 * 载入中。缺口圆环，由调用方加 animate-spin。
 * 用连续弧线而非 8 段渐隐条 —— 后者需要多层不同透明度，与"无渐变"约束冲突。
 */
export const IconSpinner: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M12 3a9 9 0 1 0 9 9" />
  </svg>
);

export const IconPlus: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/** 九宫格/工具面板 */
export const IconGrid: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

export const IconMic: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3" />
  </svg>
);

/* ---------- ToB 技术能力 ---------- */

export const IconSettings: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
  </svg>
);

export const IconDatabase: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <ellipse cx="12" cy="5.5" rx="8" ry="3.5" />
    <path d="M4 5.5v13c0 1.9 3.6 3.5 8 3.5s8-1.6 8-3.5v-13" />
    <path d="M4 12c0 1.9 3.6 3.5 8 3.5s8-1.6 8-3.5" />
  </svg>
);

export const IconCloud: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M17.5 19H9A5 5 0 1 1 9.7 9.1 6 6 0 0 1 21 11.5a4 4 0 0 1-3.5 7.5" />
  </svg>
);

/** 增长/发布语义。此前用的火箭图形带火焰尾迹，是典型的插画感来源，改为向上箭头出框 */
export const IconLaunch: React.FC<IconProps> = (p) => (
  <svg {...svgProps(p)}>
    <path d="M12 3 8 9h8z" />
    <path d="M12 9v9" />
    <path d="M7 21h10" />
  </svg>
);
